/* Collection page: live storefront filtering through the Section Rendering API, the mobile filter sheet,
   searchable filter groups and the tip finder's "my sprayer" shortcut. Without JS the filter form is a plain GET.
   Client-side filters on the cards already on the page (no Search & Discovery needed), all sharing one result count:
     segments and stock   one sprayer's parts, and stock on small lists (?segment= ?stock=)
     sprayer facets       sprayers mode (plp-sprayer-facets.liquid): ?job= ?coat= ?load= ?use= ?tip= ?flow= ?power= ?frame= ?brand=
     paint to tip         tip pages (plp-paint-tips.liquid + the shared paint-picker.js): ?paint=<id>&surface=<wall|door|fence>&sprayer=<handle>
     paint to sprayer     sprayers mode (paint-picker.liquid): ?paint=<id> keeps the sprayers whose max tip covers the paint
   Each keeps its own state, writes its own URL params and re-applies after every section re-render. */
(() => {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  $$('[data-plp]').forEach((root) => {
    const sid = root.dataset.sectionId;
    const form = $('[data-plp-form]', root);
    const sheet = $('[data-plp-sheet]', root);
    const opener = $('[data-plp-open]', root);
    const sort = $('[data-plp-sort]', root);
    const MAXKEY = 'ase.plp.maxTip';
    let timer = null;
    let ctrl = null;
    /* Client-side filters register here: clientParams write their URL params into every re-render URL (so a sort or
       filter change keeps them), clientActive names the ones currently narrowing the list (for the count). */
    const clientParams = [];
    const clientActive = new Set();

    const queryFromForm = () => {
      const fd = form ? new FormData(form) : new FormData();
      if (sort) fd.set('sort_by', sort.value);
      const qs = new URLSearchParams();
      for (const [k, v] of fd.entries()) if (String(v).trim() !== '') qs.append(k, v);
      return qs.toString();
    };
    const baseUrl = () => (form ? form.getAttribute('action') : window.location.pathname);

    async function render(url, { push = true, scroll = false } = {}) {
      if (ctrl) ctrl.abort();
      ctrl = new AbortController();
      root.classList.add('is-loading');
      const open = {};
      $$('[data-fg]', root).forEach((d) => { open[d.dataset.fg] = d.open; });
      const focusId = document.activeElement && root.contains(document.activeElement) ? document.activeElement.id : null;
      try {
        const u = new URL(url, window.location.origin);
        u.searchParams.set('section_id', sid);
        const res = await fetch(u.toString(), { signal: ctrl.signal, headers: { Accept: 'text/html' } });
        if (!res.ok) throw new Error(res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        $$('[data-plp-region]', root).forEach((el) => {
          const next = doc.querySelector(`[data-plp-region="${el.dataset.plpRegion}"]`);
          if (next) el.innerHTML = next.innerHTML;
        });
        $$('[data-fg]', root).forEach((d) => { if (d.dataset.fg in open) d.open = open[d.dataset.fg]; });
        if (focusId) { const f = document.getElementById(focusId); if (f) f.focus({ preventScroll: true }); }
        if (push) {
          const clean = new URL(url, window.location.origin);
          clean.searchParams.delete('section_id');
          history.pushState({ plp: true }, '', clean.pathname + clean.search);
        }
        applyMaxTipMarks();
        document.dispatchEvent(new CustomEvent('ase:plp-updated', { detail: { root } }));
        if (scroll) { const top = $('[data-plp-bar]', root); if (top) window.scrollTo({ top: top.getBoundingClientRect().top + window.scrollY - 70, behavior: 'smooth' }); }
      } catch (err) {
        if (err.name !== 'AbortError') window.location.href = url;
      } finally {
        root.classList.remove('is-loading');
      }
    }

    /* Spec values (orifice, mesh, fan, length) sort by number, not alphabetically: 30 mesh before 100 mesh */
    const num = (t) => { const m = String(t).match(/\d+(?:\.\d+)?/); return m ? parseFloat(m[0]) : Infinity; };
    function sortSpecs() {
      $$('.fg__list--chips, .fg__list--num, .tf__opts--mono', root).forEach((list) => {
        const kids = Array.from(list.children);
        const key = (el) => num(el.dataset.fvText || el.dataset.orifice || el.textContent);
        const sorted = kids.slice().sort((a, b) => key(a) - key(b));
        if (sorted.some((el, i) => el !== kids[i])) sorted.forEach((el) => list.appendChild(el));
      });
    }
    /* "What machine do you run?" lists models by number too: Ultra 390 before Ultra 1095 XT */
    function sortMachines() {
      $$('[data-fit-select]', root).forEach((sel) => {
        const opts = Array.from(sel.options).slice(1);
        const sorted = opts.slice().sort((a, b) => num(a.textContent) - num(b.textContent) || a.textContent.localeCompare(b.textContent));
        if (sorted.some((o, i) => o !== opts[i])) sorted.forEach((o) => sel.appendChild(o));
      });
    }
    sortSpecs();
    sortMachines();
    document.addEventListener('ase:plp-updated', sortSpecs);

    const submit = (delay = 200) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const params = new URLSearchParams(queryFromForm());
        clientParams.forEach((write) => write(params));
        const qs = params.toString();
        render(baseUrl() + (qs ? `?${qs}` : ''));
      }, delay);
    };

    /* One result count for every client-side filter: the bar, the sheet's Show button and the empty state */
    function updateCount() {
      const lis = $$('.plp-results .plp-grid > li', root);
      const on = clientActive.size > 0;
      const n = lis.filter((li) => !li.hidden && !li.hasAttribute('data-paint-out')).length;
      $$('[data-plp-n]', root).forEach((el) => {
        if (!el.dataset.total) el.dataset.total = el.textContent;
        const v = on ? n : parseInt(el.dataset.total, 10);
        el.textContent = v;
        const unit = el.parentElement && $('[data-plp-unit]', el.parentElement);
        if (unit) unit.textContent = v === 1 ? 'product' : 'products';
      });
      const empty = $('[data-seg-empty]', root);
      if (empty) empty.hidden = !(on && n === 0);
      return n;
    }

    if (form) {
      form.addEventListener('change', (e) => {
        if (e.target.matches('[data-fg-search], [data-seg-input], [data-stock-input], [data-sf-input]')) return;
        submit(e.target.matches('[data-price-input]') ? 500 : 120);
      });
      form.addEventListener('submit', (e) => { e.preventDefault(); submit(0); });
      form.addEventListener('input', (e) => {
        const s = e.target.closest('[data-fg-search]');
        if (!s) return;
        const q = s.value.trim().toLowerCase();
        const list = s.closest('[data-fg]').querySelector('.fg__list');
        list.classList.toggle('is-searching', q !== '');
        $$('li', list).forEach((li) => { li.hidden = q !== '' && !(li.dataset.fvText || '').includes(q); });
      });
    }
    if (sort) sort.addEventListener('change', () => submit(0));

    root.addEventListener('click', (e) => {
      const more = e.target.closest('[data-fg-more]');
      if (more) {
        const list = more.closest('[data-fg]').querySelector('.fg__list');
        const clamped = list.classList.toggle('is-clamped');
        more.textContent = clamped ? more.dataset.more || more.textContent : 'Show fewer';
        if (!more.dataset.more && !clamped) more.dataset.more = more.textContent;
        return;
      }
      const link = e.target.closest('[data-plp-link]');
      if (link && link.href) {
        e.preventDefault();
        render(link.href, { scroll: link.closest('.plp-pages') !== null });
        return;
      }
      const fold = e.target.closest('[data-plp-fold-btn]');
      if (fold) {
        // Phones: the paint picker and the job questions fold behind one Filter (n) button
        const open = root.classList.toggle('is-fold-open');
        fold.setAttribute('aria-expanded', String(open));
        return;
      }
      if (e.target.closest('[data-plp-open]')) { openSheet(); return; }
      if (e.target.closest('[data-plp-close]')) closeSheet();
    });

    /* Mobile filter sheet */
    const mobile = () => window.matchMedia('(max-width: 989px)').matches;
    function openSheet() {
      if (!sheet) return;
      sheet.classList.add('is-open');
      opener.setAttribute('aria-expanded', 'true');
      if (mobile()) {
        document.body.style.overflow = 'hidden';
        const panel = $('.plp-filters__panel', sheet);
        panel.setAttribute('aria-modal', 'true');
        setTimeout(() => $('.plp-filters__close', sheet).focus(), 60);
      }
    }
    function closeSheet() {
      if (!sheet || !sheet.classList.contains('is-open')) return;
      sheet.classList.remove('is-open');
      opener.setAttribute('aria-expanded', 'false');
      $('.plp-filters__panel', sheet).setAttribute('aria-modal', 'false');
      document.body.style.overflow = '';
      opener.focus({ preventScroll: true });
    }
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeSheet(); });

    /* Tip finder: "my sprayer" ticks every orifice the sprayer can run */
    const orificeInputs = () => $$('input[name*="ase.tip_orifice"]', root);
    function applyMaxTipMarks() {
      let max = NaN;
      try { max = parseFloat(sessionStorage.getItem(MAXKEY)); } catch (e) { /* storage blocked */ }
      const sel = $('[data-tf-sprayer]', root);
      if (sel && !Number.isNaN(max)) {
        const opt = Array.from(sel.options).find((o) => parseFloat(o.value) === max);
        if (opt && !sel.value) sel.value = opt.value;
      }
      $$('[data-orifice]', root).forEach((l) => l.classList.toggle('is-too-big', !Number.isNaN(max) && parseFloat(l.dataset.orifice) > max + 1e-6));
    }
    function setMaxTip(max) {
      try { if (Number.isNaN(max)) sessionStorage.removeItem(MAXKEY); else sessionStorage.setItem(MAXKEY, String(max)); } catch (e) { /* storage blocked */ }
      const inputs = orificeInputs();
      if (!inputs.length) return false;
      inputs.forEach((i) => { if (!i.disabled || i.checked) i.checked = !Number.isNaN(max) && parseFloat(i.value) <= max + 1e-6; });
      applyMaxTipMarks();
      return true;
    }
    root.addEventListener('change', (e) => {
      if (!e.target.matches('[data-tf-sprayer]')) return;
      if (setMaxTip(parseFloat(e.target.value))) submit(0);
    });

    /* "What machine do you run?": ticks only that machine in the real ase.fits_machine filter. The pick is remembered
       per viewer (localStorage), so the next parts, tips or accessories page offers "Show what fits my ..." in one tap. */
    const MACHINEKEY = 'ase.plp.machine';
    const remembered = () => { try { return localStorage.getItem(MACHINEKEY) || ''; } catch (e) { return ''; } };
    function setMachine(sel, value) {
      const inputs = $$('input', form || root).filter((i) => i.name === sel.dataset.fitParam);
      if (!inputs.length) return false;
      inputs.forEach((i) => { i.checked = value !== '' && i.value === value; });
      try { if (value) localStorage.setItem(MACHINEKEY, value); } catch (e) { /* storage blocked */ }
      return true;
    }
    function offerMine() {
      const mine = remembered();
      $$('[data-fit-select]', root).forEach((sel) => {
        const btn = sel.closest('.pi__machine') && $('[data-fit-mine]', sel.closest('.pi__machine'));
        if (!btn) return;
        const has = mine && !sel.value && Array.from(sel.options).some((o) => o.value === mine);
        btn.hidden = !has;
        if (has) { btn.textContent = `Show what fits my ${mine}`; btn.dataset.value = mine; }
      });
    }
    offerMine();
    document.addEventListener('ase:plp-updated', () => { sortMachines(); offerMine(); });
    root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-fit-mine]');
      if (!btn) return;
      const sel = $('[data-fit-select]', btn.closest('.pi__machine'));
      if (sel && setMachine(sel, btn.dataset.value)) submit(0);
    });

    root.addEventListener('change', (e) => {
      if (e.target.matches('[data-fit-select]')) { if (setMachine(e.target, e.target.value)) submit(0); }
    });

    /* ?max_tip=0.021 from a sprayer page: preselect what that sprayer runs */
    const params = new URLSearchParams(window.location.search);
    if (params.has('max_tip')) {
      const max = parseFloat(params.get('max_tip'));
      params.delete('max_tip');
      if (!Number.isNaN(max) && setMaxTip(max)) submit(0);
      else history.replaceState({}, '', window.location.pathname + (params.toString() ? `?${params}` : ''));
    } else {
      applyMaxTipMarks();
    }

    /* Segments and stock (plp-segments.liquid, the client groups in plp-facets.liquid).
       With Search & Discovery's ase.part_group filter on, a segment chip ticks only that value in the real filter.
       Until then, one sprayer's parts are all on the page and this shows one segment at a time. The stock filter works
       the same way until ase.stock_status is a filter. ?segment= and ?stock= keep the view shareable. */
    const view = { seg: new Set(), stock: new Set() };
    const fromUrl = () => {
      const q = new URLSearchParams(window.location.search);
      const list = (k) => new Set((q.get(k) || '').split(',').filter(Boolean));
      view.seg = list('segment'); view.stock = list('stock');
    };
    const toUrl = () => {
      const u = new URL(window.location.href);
      [['segment', view.seg], ['stock', view.stock]].forEach(([k, set]) => { if (set.size) u.searchParams.set(k, [...set].join(',')); else u.searchParams.delete(k); });
      history.replaceState(history.state, '', u.pathname + u.search + u.hash);
    };
    function applyView() {
      if (!root.hasAttribute('data-seg-client')) view.seg.clear();
      if (!root.hasAttribute('data-stock-client')) view.stock.clear();
      const cards = $$('.plp-results li[data-seg], .plp-results li[data-stock]', root);
      const on = view.seg.size > 0 || view.stock.size > 0;
      cards.forEach((li) => {
        li.hidden = !((!view.seg.size || view.seg.has(li.dataset.seg)) && (!view.stock.size || view.stock.has(li.dataset.stock)));
      });
      $$('[data-seg-group]', root).forEach((g) => {
        const vis = $$('li[data-seg]', g).filter((li) => !li.hidden).length;
        g.hidden = vis === 0;
        const c = $('[data-seg-n]', g);
        if (c) c.textContent = vis;
      });
      if (on) clientActive.add('view'); else clientActive.delete('view');
      updateCount();
      if (root.hasAttribute('data-seg-client')) {
        $$('[data-seg-chip]', root).forEach((c) => {
          const id = c.dataset.segChip;
          const pressed = id === '' ? view.seg.size === 0 : view.seg.size === 1 && view.seg.has(id);
          c.classList.toggle('is-on', pressed);
          c.setAttribute('aria-pressed', String(pressed));
        });
      }
      $$('[data-seg-input]', root).forEach((i) => { i.checked = view.seg.has(i.value); });
      $$('[data-stock-input]', root).forEach((i) => { i.checked = view.stock.has(i.value); });
    }
    root.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-seg-chip]');
      if (!chip) return;
      const id = chip.dataset.segChip;
      const server = chip.closest('[data-pseg]')?.dataset.psegServer;
      if (server && form) {
        $$('input', form).filter((i) => i.name === server).forEach((i) => { i.checked = id !== '' && i.value === chip.dataset.segLabel; });
        submit(0);
        return;
      }
      view.seg = id ? new Set([id]) : new Set();
      applyView();
      toUrl();
    });
    root.addEventListener('change', (e) => {
      const t = e.target;
      if (!t.matches('[data-seg-input], [data-stock-input]')) return;
      const set = t.matches('[data-seg-input]') ? view.seg : view.stock;
      if (t.checked) set.add(t.value); else set.delete(t.value);
      applyView();
      toUrl();
    });
    if (root.hasAttribute('data-seg-client') || root.hasAttribute('data-stock-client')) { fromUrl(); applyView(); }
    document.addEventListener('ase:plp-updated', (e) => {
      if (e.detail?.root !== root) return;
      applyView();
      if (view.seg.size || view.stock.size) toUrl();
    });

    /* ---------- Sprayer facets (plp-sprayer-facets.liquid, plp-sprayer-chips.liquid) ----------
       Every sprayer is on the page with data-f tokens (job:interior coat:texture tip:017 flow:c ...). OR within a group,
       AND across groups. Counts are live: each option counts the sprayers that match every OTHER group plus itself, so
       ticking a second job never zeroes the first. State lives in the URL (?job=interior,ceilings&coat=texture). */
    if (root.hasAttribute('data-sf-client')) {
      const SFG = ['base', 'job', 'coat', 'load', 'use', 'tip', 'flow', 'power', 'frame', 'brand'];
      const X = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
      let sf = new Map();
      const sfFromUrl = (search) => {
        const q = new URLSearchParams(search);
        sf = new Map();
        SFG.forEach((g) => {
          const v = q.getAll(g).join(',').split(',').map((x) => x.trim()).filter(Boolean);
          if (v.length) sf.set(g, new Set(v));
        });
      };
      const sfWrite = (params) => {
        SFG.forEach((g) => {
          params.delete(g);
          const set = sf.get(g);
          if (set && set.size) params.set(g, [...set].join(','));
        });
      };
      clientParams.push(sfWrite);
      const sfToUrl = () => {
        const u = new URL(window.location.href);
        sfWrite(u.searchParams);
        history.replaceState(history.state, '', u.pathname + u.search + u.hash);
      };
      const sfCards = () => $$('.plp-results .plp-grid > li[data-f]', root);
      const tokens = (li) => li._sf || (li._sf = new Set((li.dataset.f || '').split(/\s+/).filter(Boolean)));
      const matches = (li, skip) => {
        const t = tokens(li);
        for (const [g, set] of sf) {
          if (g === skip || !set.size) continue;
          let ok = false;
          for (const v of set) if (t.has(`${g}:${v}`)) { ok = true; break; }
          if (!ok) return false;
        }
        return true;
      };
      const labelFor = (g, v) => {
        const i = $$('[data-sf-input]', root).find((el) => el.dataset.sfGroup === g && el.value === v);
        const l = i && i.nextElementSibling && $('[data-sf-label]', i.nextElementSibling);
        return l ? l.textContent.trim() : v;
      };
      function sfApply() {
        const lis = sfCards();
        lis.forEach((li) => { li.hidden = !matches(li, null); });
        const memo = new Map();
        const count = (g, v) => {
          const key = `${g}:${v}`;
          if (!memo.has(key)) memo.set(key, lis.reduce((n, li) => n + (tokens(li).has(key) && matches(li, g) ? 1 : 0), 0));
          return memo.get(key);
        };
        $$('[data-sf-input]', root).forEach((i) => {
          const g = i.dataset.sfGroup;
          const on = !!(sf.get(g) && sf.get(g).has(i.value));
          const n = count(g, i.value);
          i.checked = on;
          i.disabled = n === 0 && !on;
          const ne = i.nextElementSibling && $('[data-sf-n]', i.nextElementSibling);
          if (ne) ne.textContent = n;
        });
        $$('[data-sf-group-box]', root).forEach((box) => {
          const k = (sf.get(box.dataset.sfGroupBox) || new Set()).size;
          const b = $('[data-sf-gn]', box);
          if (b) { b.hidden = k === 0; b.textContent = k ? k : ''; }
          if (k) box.open = true;
        });
        $$('[data-sf-chip]', root).forEach((c) => {
          const key = c.dataset.sfChip;
          const at = key.indexOf(':');
          const g = key.slice(0, at);
          const v = key.slice(at + 1);
          const on = !!(sf.get(g) && sf.get(g).has(v));
          const n = count(g, v);
          c.classList.toggle('is-on', on);
          c.classList.toggle('is-off', n === 0 && !on);
          const ne = $('[data-sf-n]', c);
          if (ne) ne.textContent = n;
        });
        $$('.pi__more', root).forEach((d) => { if ($('.qchip.is-on', d)) d.open = true; });

        const picked = [];
        sf.forEach((set, g) => set.forEach((v) => picked.push([g, v])));
        $$('[data-plp-region="fcount"]', root).forEach((el) => { el.textContent = picked.length ? picked.length : ''; });
        const act = $('[data-sf-active]', root);
        if (act) {
          act.textContent = '';
          picked.forEach(([g, v]) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'achip';
            b.dataset.sfRemove = `${g}:${v}`;
            const t = document.createElement('span');
            t.textContent = labelFor(g, v);
            const x = document.createElement('span');
            x.className = 'achip__x';
            x.setAttribute('aria-hidden', 'true');
            x.innerHTML = X;
            const h = document.createElement('span');
            h.className = 'visually-hidden';
            h.textContent = 'Remove filter';
            b.append(t, x, h);
            act.appendChild(b);
          });
          if (picked.length) {
            const c = document.createElement('button');
            c.type = 'button';
            c.className = 'active__clear';
            c.dataset.sfClear = '';
            c.textContent = 'Clear all';
            act.appendChild(c);
          }
          act.hidden = picked.length === 0;
        }
        if (picked.length) clientActive.add('sf'); else clientActive.delete('sf');
        updateCount();
      }
      root.addEventListener('change', (e) => {
        const i = e.target;
        if (!i.matches('[data-sf-input]')) return;
        const g = i.dataset.sfGroup;
        if (!sf.has(g)) sf.set(g, new Set());
        if (i.checked) sf.get(g).add(i.value); else sf.get(g).delete(i.value);
        if (!sf.get(g).size) sf.delete(g);
        sfApply();
        sfToUrl();
      });
      root.addEventListener('click', (e) => {
        const rm = e.target.closest('[data-sf-remove]');
        const clr = e.target.closest('[data-sf-clear]');
        if (!rm && !clr) return;
        e.preventDefault();
        if (clr) sf = new Map();
        else {
          const key = rm.dataset.sfRemove;
          const at = key.indexOf(':');
          const set = sf.get(key.slice(0, at));
          if (set) { set.delete(key.slice(at + 1)); if (!set.size) sf.delete(key.slice(0, at)); }
        }
        const inSheet = !!e.target.closest('[data-plp-sheet]');
        sfApply();
        sfToUrl();
        if (!inSheet) { const bar = $('[data-plp-bar] .plp-bar__count', root); if (bar) { bar.setAttribute('tabindex', '-1'); bar.focus({ preventScroll: true }); } }
      });
      /* A shared link may pick a chip that sits off the end of its sideways row on a phone: bring it into the row's view */
      const revealOn = () => $$('.qchips__row', root).forEach((row) => {
        const on = $('.qchip.is-on', row);
        if (on && row.scrollWidth > row.clientWidth) row.scrollLeft = Math.max(0, on.offsetLeft - row.offsetLeft - 16);
      });
      /* Paint picker to paint base: a picked paint whose record says water based (wb in paint-tips.json), or solvent
         (sb) or oil (ob) based, pre-selects that base when this page offers it. A base the painter picked by hand is
         left alone; clearing the paint (or picking one with no base on record) undoes only a base the paint set. */
      let autoBase = null;
      root.addEventListener('ase:paint', (e) => {
        if (!e.target.matches || !e.target.matches('[data-paint-picker]')) return;
        const p = e.detail && e.detail.paint;
        const want = p ? (p.wb ? 'water' : p.sb ? 'solvent' : p.ob ? 'oil' : null) : null;
        const offered = (v) => $$('[data-sf-input]', root).some((i) => i.dataset.sfGroup === 'base' && i.value === v);
        const cur = sf.get('base');
        const mine = !cur || !cur.size || (autoBase && cur.size === 1 && cur.has(autoBase));
        if (!mine) { autoBase = null; return; }
        if (want && offered(want)) {
          if (cur && cur.size === 1 && cur.has(want)) { autoBase = want; return; }
          sf.set('base', new Set([want]));
          autoBase = want;
        } else if (autoBase) {
          sf.delete('base');
          autoBase = null;
        } else return;
        sfApply();
        sfToUrl();
      });
      sfFromUrl(window.location.search);
      sfApply();
      revealOn();
      document.addEventListener('ase:plp-updated', (e) => {
        if (e.detail?.root !== root) return;
        sfFromUrl(window.location.search);
        sfApply();
        revealOn();
      });
    }

    /* ---------- Tip cards (plp-tip-card.liquid): pick a size ----------
       Each size <li data-size> carries its variant id, orifice, fan, price, availability and URL. Picking one swaps the
       card's specs, price, quick add variant and links (?variant=). */
    const priceText = (html) => {
      const d = document.createElement('div');
      d.innerHTML = html || '';
      return d.textContent;
    };
    const up0 = (t) => String(t || '').toUpperCase();
    function tipSelect(card, li) {
      if (!card || !li) return;
      const d = li.dataset;
      const input = $('[data-size-input]', li);
      if (input) input.checked = true;
      const spec = (k, val) => {
        const dd = $(`[data-tip-spec="${k}"]`, card);
        if (!dd) return;
        dd.textContent = val || '';
        dd.parentElement.hidden = !val;
      };
      spec('code', d.sizeCode);
      spec('orifice', d.orificeLabel);
      spec('fan', d.fanLabel);
      const price = parseInt(d.price, 10) || 0;
      const pr = $('[data-tip-price]', card);
      const ask = $('[data-tip-ask]', card);
      if (pr) pr.hidden = price <= 0;
      if (ask) ask.hidden = price > 0;
      const ph = $('[data-tip-price-html]', card);
      if (ph) ph.textContent = priceText(d.priceHtml);
      const add = $('[data-tip-add]', card);
      if (add) {
        add.dataset.quickAdd = d.variantId;
        add.hidden = !(d.available === 'true' && price > 0);
        add.setAttribute('aria-label', `${add.dataset.label || 'Add'} ${d.sizeCode || ''} to cart`.replace(/\s+/g, ' '));
      }
      $$('[data-tip-link]', card).forEach((a) => { if (d.url) a.setAttribute('href', d.url); });
      /* The size's own photo when it has one, else the family photo plus "Photo shows <code>" */
      const img = $('.pc__tip-img', card);
      const note = $('[data-tip-img-note]', card);
      if (img) {
        if (!img.dataset.defSrc) { img.dataset.defSrc = img.getAttribute('src') || ''; img.dataset.defSet = img.getAttribute('srcset') || ''; }
        const own = !!d.img;
        img.setAttribute('src', own ? d.img : img.dataset.defSrc);
        if (own ? d.srcset : img.dataset.defSet) img.setAttribute('srcset', own ? d.srcset : img.dataset.defSet);
        if (note) note.hidden = own || up0(d.code) === up0(note.dataset.defaultCode);
      }
      const st = $('[data-tip-stock]', card);
      if (st && d.stockHtml) st.innerHTML = d.stockHtml;
      const phLabel = $('.pc__media .ph__label', card);
      if (phLabel && d.sizeCode) phLabel.textContent = d.sizeCode;
    }
    root.addEventListener('change', (e) => {
      if (!e.target.matches('[data-size-input]')) return;
      tipSelect(e.target.closest('[data-tip-card]'), e.target.closest('[data-size]'));
    });

    /* ---------- Paint to tip picker on tip pages (plp-paint-tips.liquid, assets/paint-tips.json, SPEC section 2) ----------
       The shared paint picker (paint-picker.js) announces the paint with 'ase:paint'. A picked paint keeps only the tip
       sizes that suit it: orifice inside the paint's range (o, a one element o means "and up") or code in its tips
       list, and inside its fan range when one is given. Then "What are you painting?" narrows to the surface's fan band
       and "What sprayer do you run?" caps the orifice at the sprayer's max tip and keeps the families that fit it. With
       a paint and a surface the sizes left are ranked (film-build.js requiredSpeed, or closeness to the middle of the
       range) into one Best match and at most two Also suits; the rest wait behind "Show all N sizes that suit". The
       surface rules (json "also": FFLP414 walls, FFLP210 doors, FFLP312 fences) are only ever Also suits. Jacob's
       decisions of 18 Oct 2026 (tip-rank.js, FILM-BUILD-MODEL.md section 10): our field guidance for the surface leads
       as the "Field tested pick" with the maker's figures shown beside it (D2), water based enamels on doors take the
       trade trim row (D1), a Best match with no fresh in stock read gets the nearest in stock size that suits as
       "In stock now" (D3, read off the size's own stock line), and a surface the paint's sheet rules out shows the
       reason and no tip (D5), while the picker leaves such paints out once that surface is picked.
       ?paint=<id>&surface=<wall|door|fence>&sprayer=<handle> keeps it shareable. */
    const pt = $('[data-paint-tips]', root);
    if (pt && root.hasAttribute('data-paint-client')) {
      const picker = $('[data-paint-picker]', pt);
      const picksEl = $('[data-pt-picks]', pt);
      const surfInputs = $$('[data-pt-surface]', pt);
      const sprSel = $('[data-pt-sprayer]', pt);
      const R = window.ASETipRank;
      const { EPS, up, hasRule } = R;
      const CLAMP = 12;
      let data = { also: {}, surf: { wall: [8, 12], door: [4, 8], fence: [6, 10] } };
      let current = null;
      let surface = '';
      let showAll = false;
      const measurements = document.createElement('details');
      const summary = document.createElement('summary');
      summary.textContent = 'Check this setup with your measurements';
      measurements.appendChild(summary);
      const measureFields = {};
      for (const [key, label] of [
        ['tipPressurePsi', 'Pressure at the tip (psi), not the pump gauge'],
        ['density', 'Coating specific gravity'], ['highShearCp', 'High-shear or ICI viscosity (cP)'],
        ['lowShearCp', 'Low-shear intake viscosity (cP)'], ['hoseLengthM', 'Main hose length (m)'],
        ['hoseDiameterIn', 'Main hose internal diameter (in)'], ['whipLengthM', '1/4 inch whip length (m), zero if absent'],
        ['fittingEquivalentM', 'Equivalent 1/4 inch fitting length (m)'], ['riseM', 'Vertical rise (m), zero for level work']
      ]) {
        const row = document.createElement('label');
        row.textContent = label;
        const input = document.createElement('input');
        input.type = 'number'; input.step = 'any'; input.min = '0';
        row.appendChild(input);
        const field = document.createElement('p'); field.appendChild(row); measurements.appendChild(field); measureFields[key] = input;
      }
      const confirmLabel = document.createElement('label');
      const confirm = document.createElement('input'); confirm.type = 'checkbox';
      confirmLabel.appendChild(confirm);
      confirmLabel.appendChild(document.createTextNode(' I have checked the SDS against this exact sprayer manual.'));
      measurements.appendChild(confirmLabel);
      const note = document.createElement('p');
      note.textContent = 'Calculated estimates assume 70% motor-to-fluid efficiency, 65% transfer and 50% overlap. Missing measurements stay unscored. Low-shear readings are for intake only. Test pattern, draw and wet film before use.';
      measurements.appendChild(note);
      picksEl.insertAdjacentElement('afterend', measurements);
      measurements.addEventListener('change', () => apply());
      const setupInputs = () => {
        const values = Object.fromEntries(Object.entries(measureFields).filter(([, el]) => el.value !== '').map(([k, el]) => [k, Number(el.value)]));
        const out = { tipPressurePsi: values.tipPressurePsi, density: values.density, riseM: values.riseM, sdsConfirmed: confirm.checked };
        if (values.highShearCp != null) out.highViscosity = { value: values.highShearCp, unit: 'cP', shear: 'high' };
        if (values.lowShearCp != null) out.lowViscosity = { value: values.lowShearCp, unit: 'cP', shear: 'low' };
        if (['hoseLengthM','hoseDiameterIn','whipLengthM','fittingEquivalentM'].every(k => Number.isFinite(values[k]) && values[k] >= 0))
          out.hoses = [{ lengthM: values.hoseLengthM, diameterIn: values.hoseDiameterIn }, { lengthM: values.whipLengthM + values.fittingEquivalentM, diameterIn: .25 }];
        return out;
      };

      const fmtO = (x) => Number(x).toFixed(3).replace(/^0/, '');
      const sprayer = () => (sprSel && sprSel.value ? sprSel.options[sprSel.selectedIndex] : null);
      const fitsSprayer = (li, d, spr) => {
        if (!spr) return true;
        const max = parseFloat(spr.dataset.max);
        const o = parseFloat(d.orifice);
        if (!Number.isNaN(max) && (Number.isNaN(o) || o > max + EPS)) return false;
        const skus = (spr.dataset.skus || '').split(/\s+/).filter(Boolean);
        const card = $('[data-tip-card]', li);
        const fits = new Set(((card && card.dataset.fits) || '').split(/\s+/).filter(Boolean));
        return skus.some((s) => fits.has(s));
      };

      /* Tip codes named in a sprayer's in-box list: "RAC X LP 517 SwitchTip tip" is LP517, "RAC X LTX 427 and 531" is
         LTX427 and LTX531. A bare "RAC X 517" names no family, so it is left out rather than guessed. */
      const inBox = (txt) => {
        const out = [];
        (txt || '').split('|').forEach((line) => {
          let fam = '';
          line.toUpperCase().split(/[\s,]+/).forEach((w) => {
            if (/^(FFLP|LTX|WRLP|LP)$/.test(w)) fam = w;
            else if (/^(FFLP|LTX|WRLP|LP)\d{3,4}$/.test(w)) out.push(w);
            else if (fam && /^\d{3,4}$/.test(w)) out.push(fam + w);
          });
        });
        return out;
      };

      const pickLabel = (d) => {
        const bits = [];
        if (d.fan) bits.push(`${d.fan} in fan`);
        if (d.orifice) bits.push(`${fmtO(d.orifice)} orifice`);
        return bits.join(', ');
      };

      /* A size's own stock line (data-stock-html, or the card's line on a one size card) in stock or low: the same read
         the card shows, so "In stock now" never disagrees with it */
      const shipsOf = (li, s) => {
        const h = s.dataset.stockHtml || ($$('[data-size]', li).length === 1 ? (($('[data-tip-stock]', li) || {}).innerHTML || '') : '');
        return /stock--(?:in|low)\b/.test(h) ? '1' : '0';
      };
      const sizeData = (li, s) => ({ code: s.dataset.code, sizeCode: s.dataset.sizeCode, orifice: s.dataset.orifice, fan: s.dataset.fan, ships: shipsOf(li, s) });

      function apply() {
        const cards = $$('.plp-results .plp-grid > li[data-tip]', root);
        /* D5: a surface the paint's own sheet rules out: say so, rank nothing, and filter by the surface alone */
        const fit = current && surface ? R.suitedTo(current, surface) : { ok: null };
        const paint = fit.ok === false ? null : current;
        const rule = hasRule(paint);
        const spr = sprayer();
        const rankOn = !!(paint && surface && !paint.na);
        const pool = [];
        cards.forEach((li) => {
          const sizes = $$('[data-size]', li);
          const adm = new Map();
          li._ok = sizes.filter((s) => {
            const d = sizeData(li, s);
            /* Paint range or list, field and trade codes, Also suits codes, surface fan band: tip-rank.js, the same
               rules as /pages/spray-setup */
            const a = paint ? R.admit(paint, d, surface, data) : (R.inBand(d, surface, data) ? { extra: false } : null);
            if (!a || !fitsSprayer(li, d, spr)) return false;
            adm.set(s, { a, d });
            return true;
          });
          li._ok.forEach((s) => pool.push({ li, s, d: adm.get(s).d, extra: adm.get(s).a.extra }));
        });

        /* Rank: tip-rank.js (field guidance for the surface, then maker named, then data sheet range by physics) */
        const rk = rankOn ? R.rank(paint, surface, pool, data) : { best: null, also: [], inStock: null };
        const { best, also, inStock } = rk;
        const picked = new Set([best, ...also, inStock].filter(Boolean).map((x) => x.s));
        const ranked = rankOn && picked.size > 0;
        const collapse = ranked && !showAll;

        cards.forEach((li) => {
          const card = $('[data-tip-card]', li);
          const sizes = $$('[data-size]', li);
          const vis = collapse ? li._ok.filter((s) => picked.has(s)) : li._ok;
          sizes.forEach((s) => { s.hidden = !vis.includes(s); });
          const n = $('[data-tip-sizes-n]', li);
          if (n) n.textContent = vis.length;
          const line = $('[data-tip-pick]', li);
          if (!vis.length) { li.setAttribute('data-paint-out', ''); li.style.order = ''; if (line) line.hidden = true; return; }
          li.removeAttribute('data-paint-out');
          const mine = [best, inStock, ...also.filter((x) => x !== inStock)].filter((x) => x && x.li === li);
          li.style.order = ranked ? (best && best.li === li ? '-2' : mine.length ? '-1' : '') : '';
          if (line) {
            line.textContent = '';
            mine.forEach((x) => {
              const s = document.createElement('span');
              s.className = 'pc__pick-i';
              s.textContent = `${x === best ? 'Best match' : x === inStock ? 'In stock now' : 'Also suits'} ${x.code}`;
              line.appendChild(s);
            });
            line.hidden = !mine.length;
          }
          const cur = sizes.find((s) => { const i = $('[data-size-input]', s); return i && i.checked; });
          const want = mine.length ? mine[0].s : vis[0];
          if (mine.length ? cur !== want : (!cur || !vis.includes(cur))) tipSelect(card, want);
          clampSizes(li, vis);
        });

        /* Summary under the picker */
        picksEl.textContent = '';
        if (rankOn) {
          const add = (tag, t, cls) => { const el = document.createElement(tag); if (cls) el.className = cls; el.textContent = t; picksEl.appendChild(el); return el; };
          if (best && spr && window.ASEPumpPhysics) {
            const skus = (spr.dataset.skus || '').split(/\s+/);
            const machine = (data.machines || []).find(m => skus.includes(m.sku) || m.handle === spr.value);
            const result = window.ASEPumpPhysics.assess(machine || {}, paint, { orificeIn: Number(best.d.orifice), fanIn: Number(best.d.fan) }, setupInputs());
            add('p', `Will it pump it? ${result.verdict}. ${result.reason}`, 'tpp__pick-note');
            const detail = add('details', '');
            const heading = document.createElement('summary'); heading.textContent = 'Calculated checks'; detail.appendChild(heading);
            for (const [name, check] of Object.entries(result.checks)) {
              const line = document.createElement('p');
              const figures = [['requiredPsi','pump psi'],['lossPsi','loss psi'],['flowLpm','L/min'],['availableFlowLpm','available L/min'],['hydraulicHp','hydraulic HP']]
                .filter(([key]) => Number.isFinite(check[key])).map(([key,label]) => `${Number(check[key].toFixed(2))} ${label}`);
              line.textContent = `${window.ASEPumpPhysics.CHECK_LABELS[name] || name}: ${check.status}. ${check.reason}${figures.length ? ' ' + figures.join(', ') + '.' : ''}`;
              detail.appendChild(line);
            }
          }
          if (best) add('p', `Best match ${best.code}: ${pickLabel(best.d)}.`, 'tpp__pick tpp__pick--best');
          /* D2 / D1: our own pick leads, and the maker's figures stay beside it */
          if (best && rk.label) add('p', `${rk.label}.${rk.sheet ? ` ${rk.sheet.text}.` : ''}`, 'tpp__pick-note');
          /* D3: the Best match has no fresh in stock read, so the nearest size that suits and is in stock */
          if (inStock) add('p', `In stock now ${inStock.code}: ${pickLabel(inStock.d)}. ${best.code} has no stock showing today.`, 'tpp__pick');
          const alsoShown = also.filter((x) => x !== inStock);
          if (alsoShown.length) add('p', `Also suits ${alsoShown.map((x) => x.code).join(', ')}.`, 'tpp__pick');
          if (!best && !also.length) add('p', pool.length ? 'No size stands out for this paint on that surface. Ask us before you buy.' : 'No size here suits that paint on that surface.', 'tpp__pick');
          /* An estimate paint has no data sheet of its own: its wet coat is the median of similar paints' sheets */
          const filmRange = paint.physics && window.ASEPumpPhysics ? window.ASEPumpPhysics.film(paint.physics).range : null;
          const wetFilmLabel = filmRange ? [...new Set(filmRange)].join(' to ') : paint.w;
          if (best && best.speed) add('p', paint.basis === 'estimate'
            ? `Calculated estimate at SG 1.2, about 110 bar and a steady pass of about ${best.speed.toFixed(1)} m/s, ${best.code} lays about ${wetFilmLabel} µm wet per coat, the figure estimated from similar paints' data sheets.`
            : `Calculated estimate at SG 1.2, about 110 bar and a steady pass of about ${best.speed.toFixed(1)} m/s, ${best.code} lays the ${wetFilmLabel} µm wet coat the ${paint.b} data sheet asks for.`, 'tpp__pick-note');
          /* The tip already in the sprayer's box (ase.includes, e.g. "RAC X LP 517 SwitchTip tip"), when it suits too */
          const box = spr ? inBox(spr.dataset.inbox) : [];
          const own = box.find((c) => pool.some((x) => up(x.d.code) === c));
          if (own) {
            const name = spr.textContent.split(' · ')[0].trim();
            add('p', best && up(best.code) === own
              ? `That's the ${own} that comes in the box with the ${name}.`
              : `The ${name} comes with an ${own} in the box, and it suits this paint too.${best ? ` ${best.code} is the closest match if you want a second tip.` : ''}`, 'tpp__pick-note');
          }
          if (ranked && pool.length > picked.size) {
            const allText = paint.basis === 'none' ? `Show all ${pool.length} sizes in this fan range` : `Show all ${pool.length} sizes that suit`;
            const b = add('button', showAll ? (best ? 'Show the best match only' : 'Show fewer') : allText, 'tpp__all');
            b.type = 'button';
            b.setAttribute('aria-expanded', String(showAll));
            b.addEventListener('click', () => { showAll = !showAll; apply(); });
          }
          picksEl.hidden = false;
        } else if (fit.ok === false) {
          const el = document.createElement('p');
          el.className = 'tpp__pick';
          el.textContent = `${fit.text}. No tip is suggested for it there.`;
          picksEl.appendChild(el);
          picksEl.hidden = false;
        } else if (current && !surface && !current.na) {
          const el = document.createElement('p');
          el.className = 'tpp__pick-note';
          el.textContent = "Pick what you're painting to see the best match.";
          picksEl.appendChild(el);
          picksEl.hidden = false;
        } else {
          picksEl.hidden = true;
        }

        const on = rule || !!surface || !!spr;
        if (on) clientActive.add('paint'); else clientActive.delete('paint');
        const shown = updateCount();
        const msg = $('[data-empty-msg]', root);
        if (msg) {
          if (on && shown === 0 && clientActive.size === 1) {
            msg.textContent = spr && !pool.length ? `None of the tips here fit the ${spr.textContent.split(' · ')[0]}. ` : 'None of the tips here suit that paint and surface. ';
            const a = document.createElement('a');
            a.href = (picker && picker.dataset.contact) || '/pages/contact';
            a.textContent = 'Ask us which tip to use';
            msg.appendChild(a);
          } else {
            msg.textContent = 'Take one filter off to widen it, or start again.';
          }
        }
        root.classList.remove('is-paint-pending');
      }

      /* Long size lists (LTX has 104): show the first 12 that are visible, the picked one always, and a "more" button */
      function clampSizes(li, vis) {
        const box = $('[data-tip-sizes]', li);
        if (!box) return;
        let more = $('[data-tip-more]', box);
        const over = vis.length > CLAMP + 2 && !box.classList.contains('is-open');
        let k = 0;
        vis.forEach((s) => {
          const i = $('[data-size-input]', s);
          const keep = k < CLAMP || (i && i.checked);
          s.toggleAttribute('data-over', over && !keep);
          if (keep) k += 1;
        });
        $$('[data-size]', li).forEach((s) => { if (!vis.includes(s)) s.removeAttribute('data-over'); });
        if (vis.length > CLAMP + 2) {
          if (!more) {
            more = document.createElement('button');
            more.type = 'button';
            more.className = 'pc__more';
            more.setAttribute('data-tip-more', '');
            more.addEventListener('click', () => { box.classList.toggle('is-open'); clampSizes(li, $$('[data-size]', li).filter((s) => !s.hidden)); });
            box.appendChild(more);
          }
          more.hidden = false;
          more.textContent = box.classList.contains('is-open') ? 'Fewer sizes' : `All ${vis.length} sizes`;
          more.setAttribute('aria-expanded', String(box.classList.contains('is-open')));
        } else if (more) {
          more.hidden = true;
        }
      }
      $$('.plp-results .plp-grid > li[data-tip]', root).forEach((li) => clampSizes(li, $$('[data-size]', li)));

      const ptWrite = (params) => {
        if (current) params.set('paint', current.id); else params.delete('paint');
        if (surface) params.set('surface', surface); else params.delete('surface');
        const s = sprayer();
        if (s) params.set('sprayer', s.value); else params.delete('sprayer');
      };
      clientParams.push(ptWrite);
      const ptToUrl = () => {
        const u = new URL(window.location.href);
        ptWrite(u.searchParams);
        history.replaceState(history.state, '', u.pathname + u.search + u.hash);
      };

      if (picker) {
        picker.addEventListener('ase:paint', (e) => {
          if (e.detail.data) data = { ...data, ...e.detail.data };
          current = e.detail.paint || null;
          for (const k of ['tipPressurePsi','density','highShearCp','lowShearCp']) measureFields[k].value = '';
          confirm.checked = false;
          showAll = false;
          apply();
          ptToUrl();
        });
        picker.addEventListener('ase:paint-ready', (e) => { if (e.detail.data) data = { ...data, ...e.detail.data }; });
      }

      /* Surface and sprayer steps work with or without a paint */
      surfInputs.forEach((i) => { i.disabled = false; });
      if (sprSel) sprSel.disabled = false;
      /* D5: the picker lists only the paints whose sheet allows the surface picked */
      const tellPicker = () => { if (picker && picker.paintPicker && picker.paintPicker.setSurface) picker.paintPicker.setSurface(surface); };
      surfInputs.forEach((i) => i.addEventListener('change', () => { surface = i.checked ? i.value : surface; showAll = false; tellPicker(); apply(); ptToUrl(); }));
      /* A second tap on the picked surface clears it */
      surfInputs.forEach((i) => i.addEventListener('click', () => { if (i.dataset.was === '1') { i.checked = false; surface = ''; tellPicker(); apply(); ptToUrl(); } surfInputs.forEach((j) => { j.dataset.was = j.checked ? '1' : ''; }); }));
      if (sprSel) sprSel.addEventListener('change', () => { confirm.checked = false; showAll = false; apply(); ptToUrl(); });

      /* Restore ?surface= and ?sprayer= straight away (no data needed); ?paint= comes back through the picker */
      const q = new URLSearchParams(window.location.search);
      const si = surfInputs.find((i) => i.value === q.get('surface'));
      if (si) { si.checked = true; si.dataset.was = '1'; surface = si.value; tellPicker(); }
      const wantSpr = q.get('sprayer');
      if (sprSel && wantSpr && Array.from(sprSel.options).some((o) => o.value === wantSpr)) sprSel.value = wantSpr;
      if (!q.get('paint') && (surface || sprayer())) apply();
      document.addEventListener('ase:plp-updated', (e) => { if (e.detail?.root === root) apply(); });
    }

    /* ---------- Paint picker on sprayer collections (main-collection sprayers mode) ----------
       Picking a paint says its tip range and keeps only the sprayers whose ase.max_tip (li data-max-tip) covers the top
       of that range: the top of the maker's or Graco's range, the floor of an "and up" range, or the biggest tip a paint
       with no range lists. Tips links on the page carry ?paint= so the tip finder opens on the same paint. */
    const sp = root.hasAttribute('data-sf-client') && $('[data-paint-picker]', root);
    if (sp) {
      const line = $('[data-pp-tipline]', root);
      const tipsUrl = (line && line.dataset.tipsUrl) || '/collections/spray-tips';
      let paint = null;
      const fmtO = (x) => Number(x).toFixed(3).replace(/^0/, '');
      const withPaint = (href) => {
        const u = new URL(href, window.location.origin);
        if (paint) u.searchParams.set('paint', paint.id); else u.searchParams.delete('paint');
        return u.pathname + u.search + u.hash;
      };
      function spApply() {
        const api = sp.paintPicker;
        const top = paint && api ? api.topOrifice(paint) : NaN;
        const lis = $$('.plp-results .plp-grid > li[data-f]', root);
        const filter = paint && !Number.isNaN(top);
        lis.forEach((li) => {
          const mt = parseFloat(li.dataset.maxTip);
          const out = filter && (Number.isNaN(mt) || mt + 1e-6 < top);
          li.toggleAttribute('data-paint-out', out);
        });
        if (filter) clientActive.add('sprayerPaint'); else clientActive.delete('sprayerPaint');
        const n = updateCount();
        if (line) {
          line.textContent = '';
          if (paint && api) {
            const add = (t) => line.appendChild(document.createTextNode(t));
            const a = document.createElement('a');
            a.href = withPaint(tipsUrl);
            if (filter) {
              /* A paint with a range names it; one with a tip list already shows the list on the basis line above, so
                 this line only says how many sprayers run the biggest of them */
              const take = `${n === 1 ? 'One sprayer here runs' : `${n} sprayers here run`}`;
              add(api.hasRange(paint) ? `Tip ${api.rangeText(paint.o)} orifice. ${take} a ${fmtO(top)} tip. ` : `${take} the biggest of those tips, ${fmtO(top)}. `);
              a.textContent = 'See the tips that suit';
            } else if (paint.na) {
              /* The sheet (and, where we have it, our field note) says not for airless: say that, not "no range" */
              add(`${paint.note || 'No airless settings for this paint.'} ${paint.gn ? `Field tested: ${paint.gn.replace(/^Our team does not/, "our team doesn't")} ` : ''}`);
              a.href = api.askHref(paint);
              a.textContent = 'Ask us what to use instead';
            } else {
              add('No tip range on file for this paint, so every sprayer still shows. ');
              a.href = api.askHref(paint);
              a.textContent = 'Ask us';
            }
            line.appendChild(a);
            line.hidden = false;
          } else {
            line.hidden = true;
          }
        }
        $$('a[href*="/collections/"]', root).forEach((a) => {
          if (a.closest('[data-paint-picker]') || a.closest('[data-pp-tipline]')) return;
          const path = a.pathname || '';
          if (/\/collections\/[^/]*tips/.test(path)) a.setAttribute('href', withPaint(a.getAttribute('href')));
        });
        root.classList.remove('is-paint-pending');
      }
      sp.addEventListener('ase:paint', (e) => { paint = e.detail.paint || null; spApply(); });
      clientParams.push((params) => { if (paint) params.set('paint', paint.id); else params.delete('paint'); });
      document.addEventListener('ase:plp-updated', (e) => { if (e.detail?.root === root && paint) spApply(); });
    }

    window.addEventListener('popstate', () => { fromUrl(); render(window.location.href, { push: false }); });
  });
})();
